/**
 * figureSvg — SVG 그림의 `src`(data-URI) ↔ SVG 원문 변환과 루트 스탬프 읽기.
 *
 * 서버 `services/svg_sanitize.py` 의 `to_data_uri` · `from_data_uri` · `read_stamp` 를 거울처럼 따른다
 * (계약 BE-20261015 §3-1 · §6-4). 그림의 **종류 판정은 스탬프로** 한다 — `readStamp(src)` 가 값을 주면
 * 편집 가능한 SVG 그림, `null` 이면 픽셀(트레이스 포함). 목록 GET 없이 노드 `src` 만으로 판정할 수 있다.
 *
 * UTF-8 부호화를 손으로 하는 이유: 테스트 환경(jsdom 16)의 window 에 TextEncoder/TextDecoder 가 없고,
 * `unescape`/`escape` 는 폐기된 전역이다. 길이에 선형이고 홀로 선 서로게이트·깨진 바이트열은 null 로 알린다
 * (서버의 UnicodeEncodeError/UnicodeDecodeError 자리).
 */

export const SVG_DATA_URI_PREFIX = 'data:image/svg+xml;base64,';

const ROOT_TAG = /<svg\b[^>]*>/;
const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;

/** 문자열의 UTF-8 바이트 수. 홀로 선 서로게이트가 있으면 -1. */
export function utf8ByteLength(str) {
  let bytes = 0;
  for (let i = 0; i < str.length; i += 1) {
    const c = str.charCodeAt(i);
    if (c < 0x80) bytes += 1;
    else if (c < 0x800) bytes += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      const d = str.charCodeAt(i + 1);
      if (!(d >= 0xdc00 && d <= 0xdfff)) return -1;
      bytes += 4;
      i += 1;
    } else if (c >= 0xdc00 && c <= 0xdfff) return -1;
    else bytes += 3;
  }
  return bytes;
}

/** 문자열 → UTF-8 바이트(Uint8Array). 홀로 선 서로게이트가 있으면 null. */
export function utf8Encode(str) {
  const size = utf8ByteLength(str);
  if (size < 0) return null;
  const out = new Uint8Array(size);
  let p = 0;
  for (let i = 0; i < str.length; i += 1) {
    let cp = str.charCodeAt(i);
    if (cp >= 0xd800 && cp <= 0xdbff) {
      cp = 0x10000 + ((cp - 0xd800) << 10) + (str.charCodeAt(i + 1) - 0xdc00);
      i += 1;
    }
    if (cp < 0x80) out[p++] = cp;
    else if (cp < 0x800) { out[p++] = 0xc0 | (cp >> 6); out[p++] = 0x80 | (cp & 0x3f); }
    else if (cp < 0x10000) { out[p++] = 0xe0 | (cp >> 12); out[p++] = 0x80 | ((cp >> 6) & 0x3f); out[p++] = 0x80 | (cp & 0x3f); }
    else { out[p++] = 0xf0 | (cp >> 18); out[p++] = 0x80 | ((cp >> 12) & 0x3f); out[p++] = 0x80 | ((cp >> 6) & 0x3f); out[p++] = 0x80 | (cp & 0x3f); }
  }
  return out;
}

/** UTF-8 바이트(Uint8Array 또는 atob 가 준 바이너리 문자열) → 문자열. 깨진 열(잘림·과잉·서로게이트)은 null. */
export function utf8Decode(bytes) {
  const at = typeof bytes === 'string' ? (i) => bytes.charCodeAt(i) : (i) => bytes[i];
  const n = bytes.length;
  const parts = [];
  let i = 0;
  while (i < n) {
    const b = at(i);
    if (b < 0x80) { parts.push(String.fromCharCode(b)); i += 1; continue; }
    let need;
    let cp;
    let min;
    if (b >= 0xc2 && b <= 0xdf) { need = 1; cp = b & 0x1f; min = 0x80; }
    else if (b >= 0xe0 && b <= 0xef) { need = 2; cp = b & 0x0f; min = 0x800; }
    else if (b >= 0xf0 && b <= 0xf4) { need = 3; cp = b & 0x07; min = 0x10000; }
    else return null;
    if (i + need >= n) return null;
    for (let k = 1; k <= need; k += 1) {
      const c = at(i + k);
      if ((c & 0xc0) !== 0x80) return null;
      cp = (cp << 6) | (c & 0x3f);
    }
    if (cp < min || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return null;
    parts.push(String.fromCodePoint(cp));
    i += need + 1;
  }
  return parts.join('');
}

/** SVG 원문 → `data:image/svg+xml;base64,…` (서버 to_data_uri 와 같은 형식 — FE <img>·접두 검사 호환). */
export function toDataUri(svgText) {
  const bytes = utf8Encode(String(svgText));
  if (bytes === null) throw new TypeError('SVG 문자열에 올바르지 않은 유니코드 문자가 있습니다');
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return SVG_DATA_URI_PREFIX + btoa(binary);
}

/** data-URI → SVG 원문. 접두가 다르거나 base64·UTF-8 디코드가 안 되면 null (서버 from_data_uri 의 validate=True). */
export function fromDataUri(src) {
  if (typeof src !== 'string' || !src.startsWith(SVG_DATA_URI_PREFIX)) return null;
  const b64 = src.slice(SVG_DATA_URI_PREFIX.length);
  if (b64.length % 4 !== 0 || !BASE64.test(b64)) return null;
  let binary;
  try {
    binary = atob(b64);
  } catch (e) {
    return null;
  }
  return utf8Decode(binary);
}

/**
 * 루트 스탬프 → `{ figureUuid, revision }` 또는 null. 루트 태그만 정규식으로 읽는다(파싱 없음 — 서버 read_stamp 와 같게).
 * SVG 원문이든 data-URI 든 받는다. revision 이 정수 꼴이 아니면 null.
 */
export function readStamp(srcOrSvg) {
  let s = srcOrSvg;
  if (typeof s === 'string' && s.startsWith(SVG_DATA_URI_PREFIX)) s = fromDataUri(s);
  if (typeof s !== 'string') return null;
  const m = ROOT_TAG.exec(s);
  if (!m) return null;
  const found = {};
  for (const a of m[0].matchAll(/\bdata-(figure|revision)="([^"]*)"/g)) found[a[1]] = a[2];
  if (!('figure' in found) || !/^[0-9]+$/.test(found.revision || '')) return null;
  return { figureUuid: found.figure, revision: parseInt(found.revision, 10) };
}

/** `src` 가 SVG data-URI 인가(접두만 — 트레이스 등 스탬프 없는 SVG 도 true). */
export function isSvgFigure(src) {
  return typeof src === 'string' && src.startsWith(SVG_DATA_URI_PREFIX);
}

/** 편집 가능한 SVG 그림인가 = 루트 스탬프가 있다(계약 §3-1). 픽셀·트레이스는 false. */
export function isEditableFigure(src) {
  return readStamp(src) !== null;
}
